import { CSSObject, Theme } from '@mui/material'
import { insetBorder } from './insetBorder'

export type IconButtonVariant = 'contained' | 'ghost'
export type IconButtonColour = 'primary' | 'secondary' | 'error'
export type IconButtonSize = 'small' | 'medium' | 'large'

// The 28/36/40px sizes below are padding plus this icon, so the two move together
export const iconButtonIconStyle: CSSObject = {
  '& .MuiSvgIcon-root': {
    fontSize: '1.25rem',
  },
}

// Nucleo icons (the `*OMIcon` set) render a plain <svg>, not MUI's SvgIcon, so they don't carry
// MuiSvgIcon-root — this selector catches that case so the padding tuned for MUI's icons doesn't
// leave a Nucleo icon looking lost in the middle.
const rawSvgChildSelector = '&:has(> svg:not(.MuiSvgIcon-root))'
// A Nucleo icon's own inset padding reads as ~1px slimmer than a MuiSvgIcon at the same nominal
// size, so the raw-svg case gets an extra 1px on top of the button's own themed padding — expressed
// as that sum rather than a single number so it doesn't read as an arbitrary non-standard value.
export const iconButtonSizeStyles = (theme: Theme): Record<IconButtonSize, CSSObject> => ({
  large: {
    borderRadius: theme.shape['radius-lg'],
    padding: theme.spacing(2.5),
    [rawSvgChildSelector]: {
      padding: `calc(${theme.spacing(2.5)} + 1px)`,
    },
  },
  medium: {
    borderRadius: theme.shape['radius-lg'],
    padding: theme.spacing(2),
    [rawSvgChildSelector]: {
      padding: `calc(${theme.spacing(2)} + 1px)`,
    },
  },
  small: {
    borderRadius: theme.shape['radius-md'],
    padding: theme.spacing(1),
    [rawSvgChildSelector]: {
      padding: `calc(${theme.spacing(1)} + 1px)`,
    },
  },
})

type IconButtonColourStyle = {
  base: CSSObject
  hover: CSSObject
  // Held while the button can't respond — disabled, or loading where the component has an indicator
  disabled: CSSObject
}

export const iconButtonColourStyles = (
  theme: Theme
): Record<IconButtonVariant, Record<IconButtonColour, IconButtonColourStyle>> => ({
  contained: {
    primary: {
      base: { backgroundColor: theme.palette.bg.brand.default, color: theme.palette.text.brand.onBrand },
      hover: { backgroundColor: theme.palette.bg.brand.hover },
      disabled: { backgroundColor: theme.palette.bg.brand.default, color: theme.palette.text.brand.onBrand },
    },
    secondary: {
      base: {
        backgroundColor: theme.palette.bg.surface.elevated,
        color: theme.palette.text.muted,
        boxShadow: [
          insetBorder({ width: theme.strokeWidth.default, color: theme.palette.border.subtle }),
          theme.customShadows.buttonDrop,
        ].join(', '),
      },
      hover: {
        boxShadow: [
          insetBorder({ width: theme.strokeWidth.default, color: theme.palette.border.strong }),
          theme.customShadows.buttonInner,
          theme.customShadows.buttonDrop,
        ].join(', '),
      },
      disabled: {
        backgroundColor: theme.palette.bg.surface.elevated,
        color: theme.palette.text.default,
        boxShadow: [
          insetBorder({ width: theme.strokeWidth.default, color: theme.palette.border.subtle }),
          theme.customShadows.buttonInner,
          theme.customShadows.buttonDrop,
        ].join(', '),
      },
    },
    error: {
      base: { backgroundColor: theme.palette.bg.danger.default, color: theme.palette.text.danger.onDanger },
      hover: { backgroundColor: theme.palette.bg.danger.hover },
      disabled: { backgroundColor: theme.palette.bg.danger.default, color: theme.palette.text.danger.onDanger },
    },
  },
  ghost: {
    primary: {
      base: { backgroundColor: theme.palette.bg.invisible, color: theme.palette.text.brand.default },
      hover: { backgroundColor: theme.palette.bg.brand.muted, color: theme.palette.text.brand.onBrandMuted },
      disabled: { color: theme.palette.text.brand.default },
    },
    secondary: {
      base: { backgroundColor: theme.palette.bg.invisible, color: theme.palette.text.muted },
      hover: { backgroundColor: theme.palette.bg.interactive.hover, color: theme.palette.text.default },
      disabled: { color: theme.palette.text.default },
    },
    error: {
      base: { backgroundColor: theme.palette.bg.invisible, color: theme.palette.text.danger.default },
      hover: { backgroundColor: theme.palette.bg.danger.muted, color: theme.palette.text.danger.onDangerMuted },
      disabled: { color: theme.palette.text.danger.onDangerMuted },
    },
  },
})
